/**
 * A generic representation of an application of a "custom variant".
 *
 * The style system allows implementers of the `VariantData` interface to
 * provide their own class/attribute generation and selector generation logic.
 *
 * `VariantStateKey` is the string encoding representing a specific variant’s
   application. Looks something like this:
 * ```
 * "abcdef-12345=xyzhegh-98765"
 * ```
 *
 * The encoding consists of two parts, separated by =:
 * - `VariantFieldId` - The “left hand side” of the encoding. Corresponds to a
     generic variant "field"'s ID.
 * - `VariantOptionId` - The “right hand side” of the encoding. Corresponds to
 *   the id field of a specific variant option.
 *
 * **Important note:** While any subsystem can integrate with this custom
 * variant system, they must ensure that the "left hand side" `VariantFieldId`
 * value is globally unique, to avoid collisions with other subsystems.
 */
export type VariantStateKey = `${string}=${string}`;

/**
 * Given a `fieldId` and `optionId`, produce a `VariantStateKey` string encoding.
 */
export function stringifyVariantStateKey(
  fieldId: string,
  optionId: string
): VariantStateKey {
  return `${fieldId}=${optionId}`;
}

/**
 * Parse the provided `VariantStateKey` into its left-hand and right-hand parts,
 * aka the `VariantFieldId` and `VariantOptionId`, in the form of a `[string,
 * string]` tuple.
 *
 * **Note:** Improperly-encoded strings will still return two-element tuples,
 * but one or more of the elements may be `null`, so the caller is required to
 * validate the result.
 */
export function parseVariantStateKey(
  variantState: VariantStateKey
): [string, string] {
  return variantState.split('=').slice(0, 2) as [string, string];
}

const VARIANT_STATE_REGEX = /^[^=]+=[^=]+$/;
/**
 * Test whether a string contains a variant state key encoding.
 */
export function isVariantStateKey(str: string): str is VariantStateKey {
  return VARIANT_STATE_REGEX.test(str);
}
